<template>
  <div class="card">
    <ReceiptHeader type="outlet" :data="outletData" />
    <div v-if="loading" class="print-loading">
      <span>Preparing print...</span>
    </div>
    <div v-else-if="error" class="print-error">
      <span>{{ error }}</span>
    </div>
    <div v-else>
      <div class="title">Ringkasan</div>
      <div class="p-grid">
        <div class="p-col">
          <label>Kasir</label>
          <div>{{ summary.cashier.name }}</div>
        </div>
        <div class="p-col">
          <label>Waktu Mulai</label>
          <div>{{ openedAt }}</div>
        </div>
        <div class="p-col">
          <label>Cash Awal</label>
          <div>{{ summary.shift.opening_balance | currency }}</div>
        </div>
        <div class="p-col">
          <label>Tanggal Audit</label>
          <div>{{ auditDate }}</div>
        </div>
      </div>
      <div class="p-grid">
        <div class="p-col">
          <label>Status</label>
          <div>{{ summary.shift.status }}</div>
        </div>
        <div class="p-col">
          <label>Waktu Selesai</label>
          <div>{{ closedAt }}</div>
        </div>
        <div class="p-col">
          <label>Cash Akhir</label>
          <div>{{ summary.shift.closing_balance | currency }}</div>
        </div>
        <div class="p-col"></div>
      </div>
      <h6><b>Transaksi</b></h6>
      <div>
        <table class="table table-responsive">
          <thead class="thead-light">
            <tr>
              <th>No</th>
              <th>Kode Kategori</th>
              <th>Nama Kategori</th>
              <th>Jumlah Terjual</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(a_row, k) in summary.transaction_summary" :key="k">
              <td>{{ k + 1 }}</td>
              <td>{{ a_row.category_code }}</td>
              <td>{{ a_row.category_name }}</td>
              <td>{{ a_row.total_sold }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <h6><b>Pembayaran</b></h6>
      <div>
        <table class="table table-responsive">
          <thead class="thead-light">
            <tr>
              <th>No</th>
              <th>Metode Pembayaran</th>
              <th>Jumlah</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(a_row, k) in summary.payment_summary" :key="k">
              <td>{{ k + 1 }}</td>
              <td>{{ a_row.payment_method }}</td>
              <td>{{ a_row.total_amount | currency }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="print-footer-qr" v-if="qrCodeDataUrl">
        <div class="qr-label">Digital Signature</div>
        <img :src="qrCodeDataUrl" alt="QR Code" class="qr-image" />
      </div>
    </div>
  </div>
</template>

<script>
import ReceiptHeader from '@/components/ReceiptHeader.vue'
import moment from 'moment'
import QRCode from 'qrcode'
import { mapState } from 'vuex'

const PRINT_TIMEOUT = 500

export default {
  components: { ReceiptHeader },
  props: {
    summary: {
      type: Object,
      required: true,
      validator(val) {
        return (
          val &&
          val.cashier &&
          val.shift &&
          Array.isArray(val.transaction_summary) &&
          Array.isArray(val.payment_summary)
        )
      }
    }
  },
  data() {
    return {
      loading: true,
      error: '',
      qrCodeDataUrl: '',
      outlet: null,
      loadingOutlet: false
    }
  },
  computed: {
    ...mapState(['user']),
    outletData() {
      // Use fetched outlet data first
      if (this.outlet) {
        return {
          name: this.outlet.name,
          address: this.outlet.address,
          phone: this.outlet.phone
        }
      }
      // Fallback to user outlet properties if direct outlet object doesn't exist
      if (this.user) {
        return {
          name: this.user.outlet_name || 'MPOS SYSTEM',
          address: this.user.outlet_address || 'Jl. Laksana No 68 ABC',
          phone: this.user.outlet_phone || '(061) 6643780'
        }
      }
      // Default values if no user data
      return {
        name: 'MPOS SYSTEM',
        address: 'Jl. Laksana No 68 ABC',
        phone: '(061) 6643780'
      }
    },
    openedAt() {
      return this.summary && this.summary.shift && this.summary.shift.opened_at
        ? moment(this.summary.shift.opened_at).format('DD-MM-YYYY')
        : '-'
    },
    closedAt() {
      return this.summary && this.summary.shift && this.summary.shift.closed_at
        ? moment(this.summary.shift.closed_at).format('DD-MM-YYYY')
        : '-'
    },
    auditDate() {
      return this.summary && this.summary.shift && this.summary.shift.audit_date
        ? moment(this.summary.shift.audit_date).format('DD-MM-YYYY')
        : '-'
    },
    digitalSignature() {
      // cashier name + audit_date (ISO for uniqueness)
      if (
        this.summary &&
        this.summary.cashier &&
        this.summary.cashier.name &&
        this.summary.shift &&
        this.summary.shift.audit_date
      ) {
        const raw = `${this.summary.cashier.name}${this.summary.shift.audit_date}`
        return btoa(unescape(encodeURIComponent(raw)))
      }
      return ''
    }
  },
  watch: {
    digitalSignature: {
      immediate: true,
      handler(val) {
        if (val) {
          QRCode.toDataURL(val, { width: 120, margin: 1 }, (err, url) => {
            if (!err) this.qrCodeDataUrl = url
          })
        }
      }
    }
  },
  methods: {
    async getOutlet() {
      if (!this.user || !this.user.outlet_id) return

      this.loadingOutlet = true
      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/outlets/${this.user.outlet_id}`
        )
        if (response.status === 200 && response.data.success) {
          this.outlet = response.data.data.outlets
        }
      } catch (error) {
        console.error('Error fetching outlet:', error)
      } finally {
        this.loadingOutlet = false
      }
    }
  },
  mounted() {
    if (!this.summary || !this.summary.cashier || !this.summary.shift) {
      this.error = 'Data ringkasan tidak lengkap.'
      this.loading = false
      return
    }

    // Ensure user data is loaded and fetch outlet
    if (!this.user || Object.keys(this.user).length === 0) {
      this.$store
        .dispatch('setUser', { requestOptions: { method: 'GET' } })
        .then(() => {
          this.getOutlet() // Fetch outlet after user is loaded
        })
    } else {
      this.getOutlet() // Fetch outlet if user already exists
    }

    // Debug: Log outlet data
    console.log('User data:', this.user)
    console.log('Outlet data:', this.outletData)

    this.loading = false
    this.$nextTick(() => {
      setTimeout(() => {
        try {
          window.print()
          window.history.back()
        } catch (e) {
          this.error = 'Gagal mencetak ringkasan.'
        }
      }, PRINT_TIMEOUT)
    })
  }
}
</script>

<style scoped lang="scss">
.print-loading {
  text-align: center;
  font-size: 1.2em;
  margin: 2em 0;
}
.print-error {
  color: red;
  text-align: center;
  font-size: 1.2em;
  margin: 2em 0;
}
.print-footer-qr {
  display: none;
  text-align: center;
  margin-top: 2em;
}
.qr-label {
  font-size: 0.9em;
  margin-bottom: 0.5em;
}
.qr-image {
  width: 120px;
  height: 120px;
}
@media print {
  .card {
    box-shadow: none !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  .print-loading,
  .print-error {
    display: none !important;
  }
  .title {
    font-size: 1.5em;
    font-weight: bold;
    margin-bottom: 1em;
  }
  table {
    page-break-inside: avoid;
    width: 100%;
    font-size: 12px;
  }
  th,
  td {
    padding: 4px 8px;
  }
  h6 {
    margin-top: 1.5em;
    margin-bottom: 0.5em;
  }
  .print-footer-qr {
    display: block !important;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    text-align: center;
    background: white;
    padding: 8px 0 12px 0;
    z-index: 1000;
  }
  body {
    padding-bottom: 140px !important;
  }
}
</style>
